/**
 * Qué se le dice a Ops después de confirmar.
 *
 * Está en su propio módulo, y no dentro de `confirmar()`, porque no es una cadena de texto: es una
 * propiedad de seguridad. Desde BB-1617 `/confirm` devuelve un acuse de ENCOLADO, no un resultado —
 * a 40 req/s una confirmación grande dura minutos u horas. Decir "pagado" o "confirmado" aquí sería
 * afirmar que algo terminó cuando acaba de empezar, y en modo demo sería afirmar que se movió dinero
 * que no se movió.
 *
 * Como función pura se puede probar; dentro del componente, no.
 */
import { formatearNumero } from './formato';

/**
 * Aviso que se pinta cuando `mode === 'mock'`.
 *
 * ⚠️ La versión anterior decía "las cifras son datos de ejemplo" y **era falsa**: las cifras vienen de
 * BigQuery real desde el 2026-08-11 (5.090.029 viajes, 10,3 M€). `mode` refleja `BACKEND_MODE`, que
 * elige el CLIENTE DE PAGO — no el origen de los datos. Ese texto se escribió cuando todo era mock y
 * se quedó sin actualizar.
 *
 * Por qué importa aunque el error sea "hacia el lado seguro": si a Ops se le presentan cifras reales
 * etiquetadas como inventadas, o descarta información buena, o —peor— aprende a ignorar el banner. Y
 * el día que el banner diga algo importante, ya no lo lee.
 */
export const AVISO_MODO_MOCK =
  'Los datos son reales, de BigQuery. Lo que está deshabilitado es el pago: ningún botón mueve dinero todavía.';

export interface EstadoConfirmacion {
  /** Lo que devuelve el contrato. `completado` solo cuando la selección quedó vacía tras el dedupe. */
  estado: 'encolado' | 'completado';
  totalViajes: number;
  totalChunks: number;
  /** Viene del preview, no del confirm: es el modo con el que se calculó lo que Ops aprobó. */
  mode: 'mock' | 'real';
}

export interface AvisoConfirmacion {
  titulo: string;
  cuerpo: string;
}

/**
 * El TITULO viene de aquí, no del componente.
 *
 * Estaba escrito a mano como `title="Confirmado"` y se quedó ahí cuando `/confirm` pasó a encolar:
 * el cuerpo decía "Encolados 8 viajes" bajo una cabecera verde que decía "Confirmado". El título es
 * lo primero que se lee y va en verde, así que ganaba — y anulaba entero el arreglo del cuerpo.
 * Encontrado en la auditoría de diseño (2026-08-11). Si el título vive junto al cuerpo, no pueden
 * volver a contradecirse.
 */
export function mensajeDeConfirmacion(r: EstadoConfirmacion): AvisoConfirmacion {
  // `completado` NO significa pagado: significa que no había nada que enviar.
  const vacia = r.estado === 'completado';

  const cuerpo = vacia
    ? 'No habia ningun viaje que enviar: la seleccion quedo vacia tras el dedupe.'
    : `Encolados ${formatearNumero(r.totalViajes)} viajes en ${formatearNumero(r.totalChunks)} tandas. El pago se procesa en segundo plano.`;

  const cola =
    r.mode === 'mock'
      ? 'No se ha lanzado ningun pago real: esta version trabaja en modo demo.'
      : 'Sigue el avance en el registro de confirmaciones.';

  return {
    titulo: vacia ? 'Nada que enviar' : 'Encolado',
    cuerpo: `${cuerpo} ${cola}`,
  };
}
